import React, { useState } from 'react';
import { X, Loader2, Plus, Trash2, Pencil } from 'lucide-react';
import { Holding, Lot, updateHolding, computeTotals } from '../../services/holdingsService';
import { cn } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';

interface LotManagerModalProps {
  uid: string;
  holding: Holding;
  onClose: () => void;
  onSaved: () => void;
}

export function LotManagerModal({ uid, holding, onClose, onSaved }: LotManagerModalProps) {
  const [lots, setLots] = useState<Lot[]>(holding.lots ?? []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Add-lot form fields
  const [newShares, setNewShares] = useState('');
  const [newTotalCost, setNewTotalCost] = useState('');
  const [newDatePurchased, setNewDatePurchased] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [newType, setNewType] = useState<'buy' | 'sell'>('buy');
  
  const [editingLotId, setEditingLotId] = useState<string | null>(null);
  const [editShares, setEditShares] = useState('');
  const [editTotalCost, setEditTotalCost] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editType, setEditType] = useState<'buy' | 'sell'>('buy');
  
  const totals = computeTotals(lots);

  const handleAddLot = () => {
    const sharesNum = parseFloat(newShares);
    const costNum = parseFloat(newTotalCost);
    if (!newShares || !newDatePurchased || isNaN(sharesNum) || sharesNum <= 0) return;
    
    const newLot: Lot = {
      id: crypto.randomUUID(),
      type: newType,
      shares: sharesNum,
      totalCostPaid: isNaN(costNum) ? 0 : costNum,
      costPerShare: sharesNum > 0 && !isNaN(costNum) ? costNum / sharesNum : 0,
      datePurchased: new Date(newDatePurchased).toISOString(),
      source: 'manual',
    };
    
    setLots(prev => [...prev, newLot]);
    setNewShares('');
    setNewTotalCost('');
    setNewDatePurchased(new Date().toISOString().split('T')[0]);
    setNewType('buy');
    setShowAddForm(false);
  };

  const handleDeleteLot = (id: string) => {
    setLots(prev => prev.filter(l => l.id !== id));
  };

  const handleStartEdit = (lot: Lot) => {
    setEditingLotId(lot.id);
    setEditShares(lot.shares.toString());
    setEditTotalCost(lot.totalCostPaid.toString());
    setEditDate(new Date(lot.datePurchased).toISOString().split('T')[0]);
    setEditType(lot.type ?? 'buy');
  };

  const handleConfirmEdit = (id: string) => {
    const sharesNum = parseFloat(editShares);
    const costNum = parseFloat(editTotalCost);
    if (isNaN(sharesNum) || sharesNum <= 0 || !editDate) return;

    setLots(prev => prev.map(l =>
      l.id === id ? {
        ...l,
        type: editType,
        shares: sharesNum,
        totalCostPaid: isNaN(costNum) ? 0 : costNum,
        costPerShare: sharesNum > 0 && !isNaN(costNum) ? costNum / sharesNum : 0,
        datePurchased: new Date(editDate).toISOString(),
      } : l
    ));
    setEditingLotId(null);
  };

  const handleCancelEdit = () => {
    setEditingLotId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateHolding(uid, holding.ticker, { lots });
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save lots:', err);
      setError('Failed to save changes. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm">
      <div className="oi-overlay w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ink-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-serif font-medium italic tracking-tight flex items-center gap-3">
              Manage Lots <span className="text-sm font-mono text-ink-500 not-italic uppercase">{holding.ticker}</span>
            </h2>
            <p className="text-xs text-ink-500 font-medium">Track individual purchase lots for accurate cost basis.</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-ink-500 hover:text-oxblood transition-colors rounded-md hover:bg-surface-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {/* Summary bar */}
          <div className="text-xs font-medium text-ink-600 bg-surface-2 rounded-md px-4 py-3 mb-6">
            {totals.totalShares} shares &nbsp;&middot;&nbsp; Avg ${safeToFixed(totals.avgCostPerShare, 2)}/share &nbsp;&middot;&nbsp; Total cost ${safeToFixed(totals.totalCostBasis, 2)}
          </div>

          {/* Lots table */}
          {lots.length === 0 ? (
            <div className="text-center text-sm text-ink-500 py-6">
              No lots recorded yet. Add your first lot below.
            </div>
          ) : (
            <div className="mb-4 bg-surface border border-ink-200 rounded-lg overflow-hidden shrink-0">
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-left text-sm text-ink-600">
                  <thead className="bg-surface-2 text-xs uppercase tracking-wider font-medium text-ink-500 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium text-right">Shares</th>
                      <th className="px-4 py-3 font-medium text-right">Cost/Share</th>
                      <th className="px-4 py-3 font-medium text-right">Total Cost</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200">
                    {lots.map(lot => (
                      editingLotId === lot.id ? (
                        <tr key={lot.id} className="bg-oxblood-50">
                          {/* Date */}
                          <td className="px-4 py-2">
                            <input
                              type="date"
                              value={editDate}
                              onChange={e => setEditDate(e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-ink-200 rounded focus:outline-none focus:border-oxblood focus:ring-1 focus:ring-oxblood"
                            />
                          </td>

                          {/* Shares */}
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              placeholder="e.g. 6"
                              value={editShares}
                              onChange={e => setEditShares(e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-ink-200 rounded focus:outline-none focus:border-oxblood focus:ring-1 focus:ring-oxblood text-right font-mono"
                            />
                          </td>

                          {/* Cost/Share — read-only, computed live */}
                          <td className="px-4 py-2 text-right font-mono text-xs text-ink-500">
                            {parseFloat(editShares) > 0 && parseFloat(editTotalCost) > 0
                              ? `$${(parseFloat(editTotalCost) / parseFloat(editShares)).toFixed(2)}`
                              : '—'}
                          </td>

                          {/* Total Cost / Proceeds */}
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              placeholder="e.g. 1500.00"
                              value={editTotalCost}
                              onChange={e => setEditTotalCost(e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-ink-200 rounded focus:outline-none focus:border-oxblood focus:ring-1 focus:ring-oxblood text-right font-mono"
                            />
                          </td>

                          {/* Type toggle */}
                          <td className="px-4 py-2">
                            <div className="flex gap-1">
                              {(['buy', 'sell'] as const).map(t => (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setEditType(t)}
                                  className={cn(
                                    "text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-md transition-colors",
                                    editType === t
                                      ? "bg-oxblood text-paper"
                                      : "bg-surface-2 text-ink-500 hover:bg-surface"
                                  )}
                                >
                                  {t === 'buy' ? 'Buy' : 'Sell'}
                                </button>
                              ))}
                            </div>
                          </td>

                          {/* Confirm / Cancel */}
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleConfirmEdit(lot.id)}
                                disabled={!editShares || parseFloat(editShares) <= 0 || !editDate}
                                className="text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded bg-oxblood text-paper hover:bg-oxblood-800 transition-colors disabled:opacity-40"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded bg-surface-2 text-ink-600 hover:bg-surface transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                      <tr key={lot.id} className="hover:bg-surface-2 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {new Date(lot.datePurchased).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {safeToFixed(lot.shares, 4)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          ${safeToFixed(lot.costPerShare, 2)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          ${safeToFixed(lot.totalCostPaid, 2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={cn(
                            "inline-block text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded border",
                            lot.type === 'sell' || lot.dateSold
                              ? "bg-surface-2 text-ink-500 border-ink-200"
                              : "bg-success-50 text-success-700 border-success-100"
                          )}>
                            {lot.type === 'sell' || lot.dateSold ? 'Sold' : 'Bought'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleStartEdit(lot)}
                              className="text-ink-300 hover:text-oxblood transition-colors p-1 rounded hover:bg-oxblood-50 inline-flex items-center justify-center"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLot(lot.id)}
                              className="text-ink-300 hover:text-danger-600 transition-colors p-1 rounded hover:bg-danger-50 inline-flex items-center justify-center"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Add Lot toggle */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-ink-500 hover:text-oxblood transition-colors py-2"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Lot
            </button>
          )}

          {/* Add Lot form */}
          {showAddForm && (
            <div className="bg-surface-2 rounded-lg p-4 border border-ink-200">
              <h4 className="text-sm font-medium text-ink mb-3">Add New Lot</h4>
              
              <div className="mb-4">
                <label className="block text-xs font-medium uppercase tracking-wider text-ink-500 mb-2">
                  Transaction Type
                </label>
                <div className="flex gap-2">
                  {(['buy', 'sell'] as const).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNewType(t)}
                      className={cn(
                        "text-xs font-medium uppercase tracking-wider px-4 py-1.5 rounded-md transition-colors",
                        newType === t
                          ? "bg-oxblood text-paper"
                          : "bg-surface-2 text-ink-500 hover:bg-surface"
                      )}
                    >
                      {t === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-ink-500 mb-1">
                    Shares
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    placeholder="e.g. 10.5"
                    value={newShares}
                    onChange={e => setNewShares(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:border-oxblood focus:ring-1 focus:ring-oxblood text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-ink-500 mb-1">
                    {newType === 'buy' ? 'Total Cost Paid ($)' : 'Total Proceeds ($)'}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    placeholder="e.g. 1500.00"
                    value={newTotalCost}
                    onChange={e => setNewTotalCost(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:border-oxblood focus:ring-1 focus:ring-oxblood text-sm"
                  />
                  <p className="text-[10px] text-ink-500 mt-1">{newType === 'buy' ? "We'll calculate cost/share" : "Total amount received from sale"}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-ink-500 mb-1">
                    {newType === 'buy' ? 'Date Purchased' : 'Date Sold'}
                  </label>
                  <input
                    type="date"
                    value={newDatePurchased}
                    onChange={e => setNewDatePurchased(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-2 border border-ink-200 rounded-md focus:outline-none focus:border-oxblood focus:ring-1 focus:ring-oxblood text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddLot}
                  disabled={!newShares || !newDatePurchased || parseFloat(newShares) <= 0}
                  className="px-4 py-2 bg-oxblood text-paper text-xs font-medium uppercase tracking-wider rounded-lg hover:bg-oxblood-800 transition-colors disabled:opacity-50"
                >
                  Add Lot
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-transparent text-ink-600 text-xs font-medium uppercase tracking-wider rounded-lg hover:bg-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-ink-200 flex-shrink-0 bg-surface-2">
          {error && (
            <div className="mb-4 text-xs font-medium text-danger-600 bg-danger-50 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-ink-600 hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-oxblood text-paper rounded-lg text-xs font-medium uppercase tracking-wider hover:bg-oxblood-800 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
